import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from "react-query";

import {
  CampaignTestEmailsView,
  CampaignRemainedTestEmailsView,
  IdsView,
  ActivityFilterView,
  PageActivityIdentityView,
  EmailActivityTemplateFilterView,
  PageEmailActivityTemplateIdentityView,
  TemplateTagsView,
  SenderDetailsesView,
  EditorTokenView,
  ActivityMultipleDeleteView,
  SegmentIdentitiesView1,
  SegmentFilterView,
  SegmentPageSegmentView,
  CreateWorkflowResponseView,
  WorkflowCreateRequestView,
  WorkflowDetailsView,
  WorkflowPublishImpactView,
  WorkflowPublishRequestView,
  WorkflowPublishResponseView,
  PageWorkflowInfoView,
  WorkflowFilterView,
  WorkflowEmailTemplateCreateRequestView,
  WorkflowEmailTemplateCreateResponseView,
  WorkflowEmailTemplateUpdateRequestView,
  WorkflowEmailTemplateUpdateResponseView,
  WorkflowEmailTemplateView,
  WorkflowCloneRequestView,
  WorkflowTemplateFilterView,
  PageWorkflowTemplateInfoView,
  WorkflowTemplateView,
  AlbertAudienceResponseView,
  WorkflowTemplatePreviewFilterView,
  WorkflowTemplatePreviewResponseView,
  WorkflowMetricsOverviewResponseView,
  CampaignFilterView,
  PageCampaignInfoView,
  CampaignResponseView,
  ActivityIdentityView,
  ActivityDetailsView,
  AutomationListResponseView,
  CampaignListResponseView,
  TemplateGenerateResponseView,
  AutomationGenerateRequestView,
  CampaignGenerateRequestView,
  TagsView,
  CustomAttributeMergeTagsView,
  ProviderNumber,
  SmsAccountView,
  SmsSegmentCalculationView,
  WorkflowSplitStatsView,
} from "./GeneratedApi";
import { useConfigView } from "./../Config.context";
import { api } from "./apiInstance";
import { WorkflowNodesStats } from "../pages/WorkflowsBuilderPage/WorkflowsBuilder/types";

export interface ErrorEntity {
  errorCode: string;
  errorMessage: string;
}

export interface Error {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      messageDetails?: string;
      errorCodes?: ErrorEntity[];
    };
  };
}

export function useGetWorkflowTemplateDetailsByIdQuery(
  id: string,

  options?: UseQueryOptions<WorkflowTemplateView, Error>
): UseQueryResult<WorkflowTemplateView, Error> {
  const key = ["GET", `/workflow/template/${id}`, id];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowTemplateById(id);
      return result.data;
    },
    options
  );
}

export function useGetWorkflowStatsByIdQuery(
  id: string,
  options?: UseQueryOptions<WorkflowNodesStats, Error>
): UseQueryResult<WorkflowNodesStats, Error> {
  const key = ["GET", `/workflow/${id}/node/stats`, id];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowStatsById(id);
      return result.data;
    },
    options
  );
}

export function useGetWorkflowSplitStatsQuery(
  id: string,
  nodeId: string,
  options?: UseQueryOptions<WorkflowSplitStatsView, Error>
): UseQueryResult<WorkflowSplitStatsView, Error> {
  const key = ["GET", `/workflow/${id}/split/${nodeId}/stats`, id, nodeId];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowSplitStatsById(id, nodeId);
      return result.data;
    },
    options
  );
}

export function usePutWorkflowSplitWinnerMutation(
  options?: UseMutationOptions<
    void,
    Error,
    { id: string; nodeId: string; branchId: string },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  { id: string; nodeId: string; branchId: string },
  unknown
> {
  return useMutation(async ({ id, nodeId, branchId }) => {
    const result = await api.putWorkflowSplitWinnerById(id, nodeId, {
      branchId,
    });
    return result.data;
  }, options);
}

export function useDeleteWorkflowSplitWinnerMutation(
  options?: UseMutationOptions<
    void,
    Error,
    { id: string; nodeId: string },
    unknown
  >
): UseMutationResult<void, Error, { id: string; nodeId: string }, unknown> {
  return useMutation(async ({ id, nodeId }) => {
    const result = await api.deleteWorkflowSplitWinnerById(id, nodeId);
    return result.data;
  }, options);
}

export function usePutWorkflowTemplatesGetQuery(
  data: WorkflowTemplateFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<PageWorkflowTemplateInfoView, Error>
): UseQueryResult<PageWorkflowTemplateInfoView, Error> {
  const key = ["PUT", "/workflow/template/", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putWorkflowTemplateFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function usePutWorkflowsFindFilterQuery(
  data: WorkflowFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<PageWorkflowInfoView, Error>
): UseQueryResult<PageWorkflowInfoView, Error> {
  const key = ["PUT", "/workflow", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putWorkflowFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function useGetWorkflowsEmailTemplateQuery(
  id: string,
  options?: UseQueryOptions<WorkflowEmailTemplateView, Error>
): UseQueryResult<WorkflowEmailTemplateView, Error> {
  const key = ["GET", "/workflow-email-template", id, options];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowEmailTemplate(id);
      return result.data;
    },
    options
  );
}

export function useGetWorkflowMetricsOverviewQuery(
  options?: UseQueryOptions<WorkflowMetricsOverviewResponseView, Error>
): UseQueryResult<WorkflowMetricsOverviewResponseView, Error> {
  const key = ["GET", "/workflow/overview"];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowMetricsOverview();
      return result.data;
    },
    options
  );
}

export function usePostWorkflowsEmailTemplateMutation(
  options?: UseMutationOptions<
    WorkflowEmailTemplateCreateResponseView,
    Error,
    {
      data: WorkflowEmailTemplateCreateRequestView;
    },
    unknown
  >
): UseMutationResult<
  WorkflowEmailTemplateCreateResponseView,
  Error,
  {
    data: WorkflowEmailTemplateCreateRequestView;
  },
  unknown
> {
  return useMutation(
    async ({ data }: { data: WorkflowEmailTemplateCreateRequestView }) => {
      const result = await api.postWorkflowEmailTemplate(data);
      return result.data;
    },
    options
  );
}

export function usePutWorkflowsEmailTemplateMutation(
  options?: UseMutationOptions<
    WorkflowEmailTemplateUpdateResponseView,
    Error,
    {
      id: string;
      data: WorkflowEmailTemplateUpdateRequestView;
    },
    unknown
  >
): UseMutationResult<
  WorkflowEmailTemplateUpdateResponseView,
  Error,
  {
    id: string;
    data: WorkflowEmailTemplateUpdateRequestView;
  },
  unknown
> {
  return useMutation(
    async ({
      id,
      data,
    }: {
      id: string;
      data: WorkflowEmailTemplateUpdateRequestView;
    }) => {
      const result = await api.putWorkflowEmailTemplate(id, data);
      return result.data;
    },
    options
  );
}

export function usePutActivityFindFilterQuery(
  data: ActivityFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<PageActivityIdentityView, Error>
): UseQueryResult<PageActivityIdentityView, Error> {
  const key = ["PUT", "/account-html-template/find/filter", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putActivityFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function usePutActivityTemplateEmailFindFilterQuery(
  data: EmailActivityTemplateFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<PageEmailActivityTemplateIdentityView, Error>
): UseQueryResult<PageEmailActivityTemplateIdentityView, Error> {
  const key = ["PUT", "/html-template/email/find/filter", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putActivityTemplateEmailFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function useGetSenderDetailsQuery(
  options?: UseQueryOptions<SenderDetailsesView, Error>
): UseQueryResult<SenderDetailsesView, Error> {
  const key = ["GET", "/sender-details"];
  const { config } = useConfigView();
  const appName = config?.appName || "";
  return useQuery(
    key,
    async () => {
      const result = await api.getSenderDetails(undefined, appName);
      return result.data;
    },
    options
  );
}

export function useGetSenderDetailsDefaultQuery(
  options?: UseQueryOptions<SenderDetailsesView, Error>
): UseQueryResult<SenderDetailsesView, Error> {
  const key = ["GET", "/sender-details/default"];
  const { config } = useConfigView();
  const appName = config?.appName || "";
  return useQuery(
    key,
    async () => {
      const result = await api.getSenderDetailsDefault(undefined, appName);
      return { senderDetailses: [result.data] } as SenderDetailsesView;
    },
    options
  );
}

export function useGetTemplateTagQuery(
  options?: UseQueryOptions<TemplateTagsView, Error>
): UseQueryResult<TemplateTagsView, Error> {
  const key = ["GET", "/template-tag"];
  return useQuery(
    key,
    async () => {
      const result = await api.getTemplateTag();
      return result.data;
    },
    options
  );
}

export function useGetEditorTokenQuery(
  options?: UseQueryOptions<EditorTokenView, Error>
): UseQueryResult<EditorTokenView, Error> {
  const key = ["GET", "/editor/token"];
  return useQuery(
    key,
    async () => {
      const result = await api.getEditorToken();
      return result.data;
    },
    options
  );
}

export function usePutSegmentFindIdsQuery(
  data: IdsView,

  options?: UseQueryOptions<SegmentIdentitiesView1, Error>
): UseQueryResult<SegmentIdentitiesView1, Error> {
  const key = ["PUT", "/segment/find/ids", data];
  return useQuery(
    key,
    async () => {
      const result = await api.putSegmentFindIds(data);
      return result.data;
    },
    options
  );
}

export function usePutSegmentFindFilterQuery(
  data: SegmentFilterView,

  query: {
    page?: number;
    size?: number;
    sort?: string[];
    countAllCustomers?: boolean;
  },

  options?: UseQueryOptions<SegmentPageSegmentView, Error>
): UseQueryResult<SegmentPageSegmentView, Error> {
  const key = ["PUT", "/segment/find/filter", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putSegmentFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function useGetAlbertAudienceQuery(
  query: { sort: string },

  options?: UseQueryOptions<AlbertAudienceResponseView, Error>
): UseQueryResult<AlbertAudienceResponseView, Error> {
  const key = ["GET", "/segment", query];
  return useQuery(
    key,
    async () => {
      const result = await api.useGetAlbertAudience(query);
      return result.data;
    },
    options
  );
}

export function usePutWorkflowByIdMutation(
  options?: UseMutationOptions<
    WorkflowDetailsView,
    Error,
    {
      id: string;
      data: WorkflowCreateRequestView;
    },
    unknown
  >
): UseMutationResult<
  WorkflowDetailsView,
  Error,
  {
    id: string;
    data: WorkflowCreateRequestView;
  },
  unknown
> {
  return useMutation(
    async ({ id, data }: { id: string; data: WorkflowCreateRequestView }) => {
      const result = await api.putWorkflowById(id, data);
      return result.data;
    },
    options
  );
}

export function useDeleteWorkflowByIdMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      id: string;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    id: string;
  },
  unknown
> {
  return useMutation(async ({ id }: { id: string }) => {
    const result = await api.deleteWorkflowById(id);
    return result.data;
  }, options);
}

export function useGetWorkflowPublishImpactByIdQuery(
  id: string,
  options?: UseQueryOptions<WorkflowPublishImpactView, Error>
): UseQueryResult<WorkflowPublishImpactView, Error> {
  const key = ["GET", `/workflow/${id}/publish/impact`, id];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowPublishImpactById(id);
      return result.data;
    },
    options
  );
}

export function usePostWorkflowPublishByIdMutation(
  options?: UseMutationOptions<
    WorkflowPublishResponseView,
    Error,
    {
      id: string;
      data?: WorkflowPublishRequestView;
    },
    unknown
  >
): UseMutationResult<
  WorkflowPublishResponseView,
  Error,
  {
    id: string;
    data?: WorkflowPublishRequestView;
  },
  unknown
> {
  return useMutation(
    async ({ id, data }: { id: string; data?: WorkflowPublishRequestView }) => {
      const result = await api.postWorkflowPublishById(id, data);
      return result.data;
    },
    options
  );
}

export function usePutWorkflowPauseByIdMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      id: string;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    id: string;
  },
  unknown
> {
  return useMutation(async ({ id }: { id: string }) => {
    const result = await api.putWorkflowPauseById(id);
    return result.data;
  }, options);
}

export function usePutWorkflowActivateByIdMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      id: string;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    id: string;
  },
  unknown
> {
  return useMutation(async ({ id }: { id: string }) => {
    const result = await api.putWorkflowActivateById(id);
    return result.data;
  }, options);
}

export function usePutCampaignTestEmailsByIdMutation(
  options?: UseMutationOptions<
    CampaignRemainedTestEmailsView,
    Error,
    {
      id: string;

      data: CampaignTestEmailsView;
    },
    unknown
  >
): UseMutationResult<
  CampaignRemainedTestEmailsView,
  Error,
  {
    id: string;

    data: CampaignTestEmailsView;
  },
  unknown
> {
  return useMutation(
    async ({ id, data }: { id: string; data: CampaignTestEmailsView }) => {
      const result = await api.putWorkflowsTestEmailsById(id, data);
      return result.data;
    },
    options
  );
}

export function usePutActivityFavoriteByIdAndIsFavoriteMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      id: string;

      isFavorite: boolean;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    id: string;

    isFavorite: boolean;
  },
  unknown
> {
  return useMutation(
    async ({ id, isFavorite }: { id: string; isFavorite: boolean }) => {
      const result = await api.putActivityFavoriteByIdAndIsFavorite(
        id,
        isFavorite
      );
      return result.data;
    },
    options
  );
}

export function usePutActivityTemplateEmailFavoriteByIdAndIsFavoriteMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      id: string;

      isFavorite: boolean;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    id: string;

    isFavorite: boolean;
  },
  unknown
> {
  return useMutation(
    async ({ id, isFavorite }: { id: string; isFavorite: boolean }) => {
      const result =
        await api.putActivityTemplateEmailFavoriteByIdAndIsFavorite(
          id,
          isFavorite
        );
      return result.data;
    },
    options
  );
}

export function usePostWorkflowMutation(
  options?: UseMutationOptions<
    CreateWorkflowResponseView,
    Error,
    {
      data: WorkflowCreateRequestView;
    },
    unknown
  >
): UseMutationResult<
  CreateWorkflowResponseView,
  Error,
  {
    data: WorkflowCreateRequestView;
  },
  unknown
> {
  return useMutation(async ({ data }: { data: WorkflowCreateRequestView }) => {
    const result = await api.postWorkflow(data);
    return result.data;
  }, options);
}

export function useGetWorkflowByIdQuery(
  id: string | any,
  options?: UseQueryOptions<WorkflowDetailsView, Error>
): UseQueryResult<WorkflowDetailsView, Error> {
  const key = ["GET", `/workflow/${id}`, id];
  return useQuery(
    key,
    async () => {
      const result = await api.getWorkflowById(id);
      return result.data;
    },
    options
  );
}

export function usePostWorkflowCloneMutation(
  options?: UseMutationOptions<
    CreateWorkflowResponseView,
    Error,
    {
      data: WorkflowCloneRequestView;
    },
    unknown
  >
): UseMutationResult<
  CreateWorkflowResponseView,
  Error,
  {
    data: WorkflowCloneRequestView;
  },
  unknown
> {
  return useMutation(async ({ data }: { data: WorkflowCloneRequestView }) => {
    const result = await api.postWorkflowClone(data);
    return result.data;
  }, options);
}

export function useDeleteActivityMutation(
  options?: UseMutationOptions<
    void,
    Error,
    {
      data: ActivityMultipleDeleteView;
    },
    unknown
  >
): UseMutationResult<
  void,
  Error,
  {
    data: ActivityMultipleDeleteView;
  },
  unknown
> {
  return useMutation(async ({ data }: { data: ActivityMultipleDeleteView }) => {
    const result = await api.deleteActivity(data);
    return result.data;
  }, options);
}

export function usePutWorkflowEmailTemplatePreviewGetQuery(
  data: WorkflowTemplatePreviewFilterView,
  options?: UseQueryOptions<WorkflowTemplatePreviewResponseView, Error>
): UseQueryResult<WorkflowTemplatePreviewResponseView, Error> {
  const key = ["PUT", "/workflow-email-template/preview'", data];
  return useQuery(
    key,
    async () => {
      const result = await api.putWorkflowEmailTemplatePreview(data);
      return result.data;
    },
    options
  );
}

export function usePutCampaignFindFilterQuery(
  data: CampaignFilterView,

  query: { page?: number; size?: number; sort?: string[] },

  options?: UseQueryOptions<PageCampaignInfoView, Error>
): UseQueryResult<PageCampaignInfoView, Error> {
  const key = ["PUT", "/campaign/find/filter", data, query];
  return useQuery(
    key,
    async () => {
      const result = await api.putCampaignFindFilter(data, query);
      return result.data;
    },
    options
  );
}

export function useGetCampaignByIdQuery(
  id: string | any,

  options?: UseQueryOptions<CampaignResponseView, Error>
): UseQueryResult<CampaignResponseView, Error> {
  const key = ["GET", `/campaign/${id}`, id];
  return useQuery(
    key,
    async () => {
      const result = await api.getCampaignById(id);
      return result.data;
    },
    options
  );
}

export function usePostActivityMutation(
  options?: UseMutationOptions<
    ActivityIdentityView,
    Error,
    {
      data: ActivityDetailsView;
    },
    unknown
  >
): UseMutationResult<
  ActivityIdentityView,
  Error,
  {
    data: ActivityDetailsView;
  },
  unknown
> {
  return useMutation(async ({ data }: { data: ActivityDetailsView }) => {
    const result = await api.postActivity(data);
    return result.data;
  }, options);
}

export function useGetCustomerTagsQuery(
  options?: UseQueryOptions<TagsView, Error>
): UseQueryResult<TagsView, Error> {
  const key = ["GET", "/customer/tags"];
  return useQuery(
    key,
    async () => {
      const result = await api.getCustomerTags();
      return result.data;
    },
    options
  );
}

export function useGetCustomAttributeMergeTagsQuery(
  options?: UseQueryOptions<CustomAttributeMergeTagsView, Error>
): UseQueryResult<CustomAttributeMergeTagsView, Error> {
  const key = ["GET", "/custom-attributes/merge-tag"];
  return useQuery(
    key,
    async () => {
      const result = await api.getCustomAttributeMergeTags();
      return result.data;
    },
    options
  );
}

export function useGetAutomationListQuery(
  options?: UseQueryOptions<AutomationListResponseView, Error>
): UseQueryResult<AutomationListResponseView, Error> {
  const key = ["GET", "/automation/list"];
  return useQuery(
    key,
    async () => {
      const result = await api.getAutomationList();
      return result.data;
    },
    options
  );
}

export function useGetCampaignListQuery(
  options?: UseQueryOptions<CampaignListResponseView, Error>
): UseQueryResult<CampaignListResponseView, Error> {
  const key = ["GET", "/campaign/list"];
  return useQuery(
    key,
    async () => {
      const result = await api.getCampaignList();
      return result.data;
    },
    options
  );
}

export function usePutAutomationGenerateMutation(
  options?: UseMutationOptions<
    TemplateGenerateResponseView,
    Error,
    {
      data: AutomationGenerateRequestView;
    },
    unknown
  >
): UseMutationResult<
  TemplateGenerateResponseView,
  Error,
  {
    data: AutomationGenerateRequestView;
  },
  unknown
> {
  return useMutation(
    async ({ data }: { data: AutomationGenerateRequestView }) => {
      const result = await api.putAutomationGenerate(data);
      return result.data;
    },
    options
  );
}

export function usePutCampaignGenerateMutation(
  options?: UseMutationOptions<
    TemplateGenerateResponseView,
    Error,
    {
      data: CampaignGenerateRequestView;
    },
    unknown
  >
): UseMutationResult<
  TemplateGenerateResponseView,
  Error,
  {
    data: CampaignGenerateRequestView;
  },
  unknown
> {
  return useMutation(
    async ({ data }: { data: CampaignGenerateRequestView }) => {
      const result = await api.putCampaignGenerate(data);
      return result.data;
    },
    options
  );
}

export function useGetSmsAccountQuery(
  options?: UseQueryOptions<SmsAccountView, Error>
): UseQueryResult<SmsAccountView, Error> {
  const key = ["GET", "/sms/account"];
  return useQuery(
    key,
    async () => {
      const result = await api.getSmsAccount();
      return result.data;
    },
    { staleTime: 60 * 1000, ...options }
  );
}

export function usePostSmsSegmentCalculatorMutation(
  options?: UseMutationOptions<SmsSegmentCalculationView, Error, string>
): UseMutationResult<SmsSegmentCalculationView, Error, string> {
  return useMutation(async (body: string) => {
    const result = await api.postSmsSegmentCalculator({ body });
    return result.data;
  }, options);
}

export function useGetNumbersQuery(
  options?: UseQueryOptions<ProviderNumber[], Error>
): UseQueryResult<ProviderNumber[], Error> {
  const key = ["GET", "/sms/numbers"];
  return useQuery(
    key,
    async () => {
      const result = await api.getNumbers();
      return result.data;
    },
    options
  );
}
